import { useEffect, useState } from "react";
import {
  FiEye,
  FiFileText,
  FiX,
  FiExternalLink,
  FiCalendar,
} from "react-icons/fi";
import { FaSpinner } from "react-icons/fa";
import LabOrderService from "../../../../Services/LabOrderService";

export default function InvestigationSummary({
  patientDetails,
  patientId,
  admission,
}) {
  const [labReports, setLabReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  const effectivePatientId = patientId || patientDetails?.patientId || "";

  const fetchLabReports = async () => {
    if (!effectivePatientId) return;
    setLoading(true);
    setError(null);
    const payload = {
      patientId: effectivePatientId,
      reportUploaded: "Y",
      status: 1,
      patientClass: "IPD",
    };
    try {
      const res = await LabOrderService.LabReportUploadGet(payload);
      const data = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
      setLabReports(data);
    } catch (err) {
      console.error("Failed to fetch lab reports:", err);
      setError("Failed to load investigation results.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabReports();
  }, [effectivePatientId]);

  const getDocumentUrl = (docName) => {
    if (!docName) return "";
    if (String(docName).startsWith("http")) return docName;
    const baseUrl =
      process.env.REACT_APP_IMAGE_URL ||
      "https://shadow-nonprod-application-data.s3.us-east-2.amazonaws.com/";
    return `${baseUrl}${docName}`;
  };

  const handleOpenNewTab = (docName) => {
    const url = getDocumentUrl(docName);
    if (url) window.open(url, "_blank");
  };

  const isPdf = (docName, docType) => {
    if (docType && String(docType).toLowerCase() === "pdf") return true;
    return Boolean(docName && String(docName).toLowerCase().endsWith(".pdf"));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow">
      <h3 className="text-sm font-bold text-teal-700 mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-teal-600"></span>
          5. Investigation Summary (Read Only)
        </span>
        {!loading && labReports.length > 0 && (
          <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-100">
            {labReports.length} {labReports.length === 1 ? "Report" : "Reports"}
          </span>
        )}
      </h3>

      {loading && (
        <div className="flex items-center gap-2 py-4 text-[12px] text-gray-400">
          <FaSpinner className="animate-spin" />
          Loading lab results...
        </div>
      )}

      {error && !loading && (
        <p className="text-[12px] text-red-500 py-2">{error}</p>
      )}

      {!loading && !error && labReports.length === 0 && (
        <p className="text-[12px] text-gray-400 py-2 italic">
          No uploaded lab investigation reports found.
        </p>
      )}

      {/* 2 cards in a row grid with inner scroll */}
      {!loading && !error && labReports.length > 0 && (
        <div className="max-h-[380px] overflow-y-auto pr-1.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {labReports.map((report, idx) => {
              const testName =
                report.labTestName ||
                report.testName ||
                report.orderName ||
                `Lab Report ${idx + 1}`;
              const docName =
                report.resultDocumentName ||
                report.documentName ||
                report.reportUrl ||
                "";
              const docType = report.resultDocumentType || "";
              const dateStr =
                report.uploadedDateTime ||
                report.createdDate ||
                report.modifiedDate ||
                "";

              return (
                <div
                  key={report.id || report.labTestOrderId || idx}
                  className="bg-white border border-gray-200 rounded-lg p-3.5 hover:border-teal-400 hover:shadow-sm transition flex flex-col justify-between gap-3"
                >
                  {/* Lab Card Header */}
                  <div className="flex items-start gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-md bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0 mt-0.5">
                      <FiFileText className="text-teal-600 text-sm" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4
                        className="text-xs font-bold text-gray-800 truncate"
                        title={testName}
                      >
                        {testName}
                      </h4>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {report.labTestCode && (
                          <span className="text-[10px] font-mono font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            {report.labTestCode}
                          </span>
                        )}
                        {report.diagnosticType && (
                          <span className="text-[10px] font-semibold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded border border-teal-100">
                            {report.diagnosticType}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-[11px] text-gray-400">
                    <span className="flex items-center gap-1 truncate text-gray-500">
                      <FiCalendar className="text-[10px]" />
                      {dateStr ? dateStr.substring(0, 16) : "Uploaded"}
                    </span>

                    {docName ? (
                      <button
                        onClick={() => setSelectedReport(report)}
                        className="flex items-center bg-white gap-1.5 px-3 py-1 text-xs font-semibold text-teal-700  hover:bg-teal-100 border border-teal-200 rounded-md transition cursor-pointer shadow-2xs"
                      >
                        <FiEye className="text-xs" /> View
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs italic">
                        No document
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Document Preview Popup Modal */}
      {selectedReport && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5"
          onClick={() => setSelectedReport(null)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2.5 min-w-0">
                <FiFileText className="text-teal-600 text-lg shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-gray-800 truncate">
                    {selectedReport.labTestName || "Lab Investigation Report"}
                  </h3>
                  <p className="text-[11px] text-gray-500">
                    {selectedReport.uploadedDateTime ||
                      selectedReport.createdDate ||
                      ""}
                    {selectedReport.diagnosticType
                      ? ` • ${selectedReport.diagnosticType}`
                      : ""}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedReport(null)}
                  className="p-1.5 text-gray-400 bg-white hover:text-gray-700 hover:bg-gray-200 rounded-full transition cursor-pointer"
                  title="Close"
                >
                  <FiX className="text-lg" />
                </button>
              </div>
            </div>

            {/* Modal Document Body */}
            <div className="p-4 overflow-y-auto flex-1 bg-slate-100 flex items-center justify-center min-h-[450px]">
              {(() => {
                const docName =
                  selectedReport.resultDocumentName ||
                  selectedReport.documentName ||
                  selectedReport.reportUrl ||
                  "";
                const url = getDocumentUrl(docName);

                if (!url) {
                  return (
                    <p className="text-sm text-gray-500 italic">
                      No document URL available for this report.
                    </p>
                  );
                }

                if (isPdf(docName, selectedReport.resultDocumentType)) {
                  return (
                    <iframe
                      src={`${url}#zoom=page-width`}
                      title={selectedReport.labTestName || "Lab Report"}
                      className="w-full h-[65vh] rounded-md border border-gray-300 bg-white shadow-xs"
                    />
                  );
                }

                return (
                  <img
                    src={url}
                    alt={selectedReport.labTestName || "Lab Report"}
                    className="max-h-[70vh] max-w-full rounded-md object-contain shadow-xs bg-white"
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
